import type { July4848PromoMarketTier } from '../utils/get-july-48-48-promo-variant';
import { getJuly4848PromoMarketTier } from '../utils/get-july-48-48-promo-variant';

/**
 * CONENG-2513 — 48/48 July Flash Promo. Two market tiers map to Sanity `promotionId` values
 * (currency-restricted in CMS). Gate on `promotionId`, never on `promoCode` (mutable Braavos coupon).
 *
 * G1 (`48july48`) — full-price markets, 50% off ("48% off, for 48 hours").
 * G2 (`48july10`) — new-price markets, 10% off ("Limited-time price drop").
 */
export type July4848PromoVariant = 'g1' | 'g2';

export const JULY_48_48_PROMO_CTA_LABEL = 'Save Now';
export const JULY_48_48_PROMO_EYEBROW = 'FLASH SALE';

/** Shared blue→purple gradient (Figma 1672:3880 / 1672:4034). */
export const JULY_48_48_PROMO_GRADIENT = 'linear-gradient(133.46deg, #0035B2 15.55%, #380066 70.31%)';
export const JULY_48_48_PROMO_BORDER_COLOR = '#175CFF';
export const JULY_48_48_PROMO_EYEBROW_COLOR = '#AFC6FF';
export const JULY_48_48_PROMO_SUBTITLE_COLOR = '#EBBAFF';
export const JULY_48_48_PROMO_HEADLINE_COLOR = 'white';
export const JULY_48_48_PROMO_CTA_COLOR = '#175CFF';

/**
 * Checkout deep link by market tier. Full-price (G1) checks out on the 4-month bundle; new-price
 * (G2) on the annual plan. `getJuly4848PromoCheckoutHref` appends the live coupon as `?coupon=`.
 */
export const JULY_48_48_PROMO_CHECKOUT_HREF: Record<July4848PromoMarketTier, string> = {
  full: '/checkout/individual/4month/credit-card',
  low: '/checkout/individual/annually/credit-card',
};

type July4848PromoVariantConfig = {
  catalogBg: string;
  catalogBgMobile: string;
  experienceAtfBg: string;
  headlineLine1: string;
  headlineLine2: string;
  homepageBg: string;
  homepageBgMobile: string;
  subtitle: string;
};

export const JULY_48_48_PROMO_VARIANTS: Record<July4848PromoVariant, July4848PromoVariantConfig> = {
  g1: {
    catalogBg: '/images/promotions/july-48-48-promo/catalog-bg.png',
    catalogBgMobile: '/images/promotions/july-48-48-promo/catalog-bg.png',
    experienceAtfBg: '/images/promotions/july-48-48-promo/experience-atf-bg.png',
    headlineLine1: '48% off,',
    headlineLine2: 'for 48 hours',
    homepageBg: '/images/promotions/july-48-48-promo/homepage-bg.png',
    homepageBgMobile: '/images/promotions/july-48-48-promo/homepage-bg-mobile.png',
    subtitle: 'All AI engineering, data & product courses.',
  },
  g2: {
    catalogBg: '/images/promotions/july-48-48-promo/catalog-bg.png',
    catalogBgMobile: '/images/promotions/july-48-48-promo/catalog-bg.png',
    experienceAtfBg: '/images/promotions/july-48-48-promo/experience-atf-bg.png',
    headlineLine1: 'Limited-time',
    headlineLine2: 'price drop',
    homepageBg: '/images/promotions/july-48-48-promo/homepage-bg.png',
    homepageBgMobile: '/images/promotions/july-48-48-promo/homepage-bg-mobile.png',
    subtitle: 'On all the latest AI & tech courses.',
  },
};

/**
 * Variant checkout link with the live coupon appended as `?coupon=<promoCode>`. The coupon also
 * auto-applies from the `COUPON_COOKIE` that `usePromotion` writes; the query param is a fallback
 * in case auto-apply does not run. When absent, the bare tier link is returned.
 */
export function getJuly4848PromoCheckoutHref(variant: July4848PromoVariant, promoCode?: string | null): string {
  const href = JULY_48_48_PROMO_CHECKOUT_HREF[getJuly4848PromoMarketTier(variant)];
  if (!promoCode) {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}coupon=${encodeURIComponent(promoCode)}`;
}
